/**
 * Google Translate embed for translating dynamic / DB-backed content
 * (product descriptions, blog copy, admin-authored content) that isn't
 * covered by the i18next string catalog.
 *
 * We inject the Google Translate Element script ONCE, hide the default
 * banner/UI (this keeps the Hopsy aesthetic intact), and programmatically
 * switch languages by setting the `googtrans` cookie and reloading the
 * Google Translate iframe select.
 *
 * Design decision: we only let Google touch content inside elements with
 * the `.gt-translate` class or inside `<main>`. Brand navigation and
 * form inputs are wrapped in `notranslate` so we never garble them.
 */

const GT_SCRIPT_ID = "google-translate-script";
const GT_ELEMENT_ID = "google_translate_element";

function setGtCookie(targetLang) {
  // Google's cookie format: /auto/<target>  — domain `.hopsy-...` or blank
  const value = `/auto/${targetLang}`;
  document.cookie = `googtrans=${value}; path=/`;
  const host = window.location.hostname;
  if (host && host !== "localhost") {
    // Try both bare and leading-dot scope for subdomain propagation.
    document.cookie = `googtrans=${value}; path=/; domain=${host}`;
    const parts = host.split(".");
    if (parts.length > 2) {
      document.cookie = `googtrans=${value}; path=/; domain=.${parts.slice(-2).join(".")}`;
    }
  }
}

function clearGtCookie() {
  document.cookie = "googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  const host = window.location.hostname;
  if (host && host !== "localhost") {
    document.cookie = `googtrans=; path=/; domain=${host}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    const parts = host.split(".");
    if (parts.length > 2) {
      document.cookie = `googtrans=; path=/; domain=.${parts.slice(-2).join(".")}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  }
}

function ensureHiddenContainer() {
  if (document.getElementById(GT_ELEMENT_ID)) return;
  const el = document.createElement("div");
  el.id = GT_ELEMENT_ID;
  el.style.position = "absolute";
  el.style.left = "-9999px";
  el.style.top = "-9999px";
  el.setAttribute("aria-hidden", "true");
  document.body.appendChild(el);
}

function ensureBannerNuker() {
  if (window.__gtBannerNukerOn) return;
  window.__gtBannerNukerOn = true;
  // Remove Google's top banner + any leftover body offset on every paint.
  const nuke = () => {
    document.querySelectorAll(
      ".goog-te-banner-frame, iframe.skiptranslate, .VIpgJd-ZVi9od-aZ2wEe-wOHMyf"
    ).forEach((n) => { n.style.display = "none"; n.style.visibility = "hidden"; });
    if (document.body && document.body.style.top) {
      document.body.style.top = "0px";
      document.body.style.position = "static";
    }
  };
  // Run once immediately + on DOM mutations.
  nuke();
  try {
    const mo = new MutationObserver(nuke);
    mo.observe(document.documentElement, { childList: true, subtree: true });
  } catch { /* noop */ }
  // Safety net — also run on interval for first 10s after load.
  let ticks = 20;
  const iv = setInterval(() => { nuke(); if (--ticks <= 0) clearInterval(iv); }, 500);
}

function injectHidingStyles() {
  if (document.getElementById("gt-hide-styles")) return;
  const s = document.createElement("style");
  s.id = "gt-hide-styles";
  s.textContent = `
    /* Hide Google Translate's banner frame, tooltip pill + highlight */
    .skiptranslate iframe,
    iframe.skiptranslate,
    .goog-te-banner-frame,
    iframe.goog-te-banner-frame,
    .VIpgJd-ZVi9od-aZ2wEe-wOHMyf,
    .VIpgJd-ZVi9od-aZ2wEe,
    .VIpgJd-ZVi9od-ORHb-OEVmcd { display: none !important; visibility: hidden !important; height: 0 !important; }
    body { top: 0 !important; position: static !important; }
    html { margin-top: 0 !important; }
    .goog-tooltip, .goog-tooltip:hover { display: none !important; }
    .goog-text-highlight { background-color: transparent !important; box-shadow: none !important; }
    #goog-gt-tt, .goog-te-gadget-simple, .goog-te-menu-value { display: none !important; }
  `;
  document.head.appendChild(s);
}

export function initGoogleTranslate() {
  if (typeof window === "undefined") return;
  // Inject the hiding styles FIRST so Google's banner never flashes.
  injectHidingStyles();
  // And inject a continuous DOM watcher that nukes the banner body on sight —
  // Google Translate sometimes re-mounts it after navigation.
  ensureBannerNuker();
  ensureHiddenContainer();
  if (document.getElementById(GT_SCRIPT_ID)) return;

  window.googleTranslateElementInit = function () {
    // Shape a single-page Translate element. We won't show its UI —
    // we only need its internal select so we can drive it programmatically.
    // eslint-disable-next-line no-undef
    new google.translate.TranslateElement(
      {
        pageLanguage: "en",
        includedLanguages: "en,hi,gu,bn",
        autoDisplay: false,
      },
      GT_ELEMENT_ID,
    );
  };

  const s = document.createElement("script");
  s.id = GT_SCRIPT_ID;
  s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  s.defer = true;
  document.body.appendChild(s);
}

/** Switch the whole page's dynamic text to `lang`. `"en"` restores original. */
export function setGoogleTranslateLanguage(lang) {
  if (typeof window === "undefined") return;
  const target = (lang || "en").toLowerCase();
  if (target === "en") {
    clearGtCookie();
    // A reload restores the original English DOM instantly — cheapest path.
    window.location.reload();
    return;
  }
  setGtCookie(target);
  // If Translate is already active this reloads the DOM with the target
  // cookie set, so Google re-translates on mount.
  window.location.reload();
}
